import { cn } from "@newxsystems/shared";
import { DaylightAction } from "../DaylightAction/DaylightAction";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import { TextEffect } from "../../../../text-effects/TextEffect.client";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { ICallToActionStackedProps } from "./CallToActionStacked.props";

const widths = { small: "w-1/2", medium: "w-3/4", large: "w-full" } as const;

export const CallToActionStacked = ({
  alignment,
  buttons,
  description,
  eyebrow,
  heading,
  headingEffect,
  image,
  imageWidth,
}: ICallToActionStackedProps) => {
  const centered = alignment === "center";
  return (
    <div
      className={cn("relative z-10 flex flex-col gap-5", centered && "items-center text-center")}
      data-reveal="stack"
    >
      {eyebrow ? <p className="signal-label text-primary-foreground">{eyebrow}</p> : null}
      {heading ? (
        <h2
          className="max-w-[16ch] break-words font-daylight-display text-[clamp(2.25rem,5.2vw,3.5rem)] font-semibold leading-[0.98] tracking-[-0.035em]"
          data-reveal-enter="wipe"
        >
          <TextEffect effect={headingEffect} text={heading} />
        </h2>
      ) : null}
      {description ? (
        <DaylightRichText
          className="max-w-[54ch] text-primary-foreground/75 [&_a]:text-primary-foreground [&_strong]:text-primary-foreground"
          content={description}
        />
      ) : null}
      {image ? (
        <DaylightMedia
          aspectRatio={image.aspectRatio || "16:9"}
          className={cn("my-4 ring-primary-foreground/25", widths[imageWidth || "medium"])}
          imageClassName="object-cover"
          media={image.image}
          effect={image.effect}
          sizes="(min-width: 1024px) 70vw, 100vw"
        />
      ) : null}
      {buttons?.length ? (
        <div className={cn("flex flex-wrap gap-3 pt-3", centered && "justify-center")}>
          {buttons.map((button, index) => (
            <DaylightAction action={button} key={`${button.href}-${index}`} tone="onAccent" />
          ))}
        </div>
      ) : null}
    </div>
  );
};

CallToActionStacked.displayName = "Content::CallToActionStacked";
